import { CollapsableContainer, MinimalHeaderToggle, WorksiteStatus, RecapItem } from '@components';
import { Container, Typography } from '@design-system';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import Slider from 'react-slick';
import { useWorksiteResultsPageVM } from './useWorksiteResultsPageVM';
import './WorksiteResultsPage.scss';

export const WorksiteResultsPage: React.FC = () => {
    const { worksitesWithData, currentWorksite, tasks, setNav1, currentIndex, setCurrentIndex, isLoading } = useWorksiteResultsPageVM();

    if (isLoading || !currentWorksite || typeof currentIndex !== 'number') {
        return (
            <Container>
                <Typography intrinsic="p" color="grey">
                    Chargement...
                </Typography>
            </Container>
        );
    }

    const getStatusLabel = (status: string): string => {
        const statusMap: Record<string, string> = {
            pending: 'En cours',
            completed: 'Terminée',
            done: 'Terminée',
            'not-started': 'Pas démarrée',
            not_started: 'Pas démarrée'
        };
        return statusMap[status.toLowerCase()] || status;
    };

    const getStatusColor = (status: string): 'green' | 'yellow' | 'grey' => {
        const normalizedStatus = status.toLowerCase();
        if (normalizedStatus === 'completed' || normalizedStatus === 'done') return 'green';
        if (normalizedStatus === 'pending') return 'yellow';
        return 'grey';
    };

    const getRatio = (percentConsumed: number): 'positive' | 'negative' | 'neutral' => {
        if (percentConsumed === 0) return 'neutral';
        return percentConsumed <= 100 ? 'positive' : 'negative';
    };

    return (
        <div className="worksite-results-page">
            <Slider
                ref={(slider) => setNav1(slider)}
                slidesToShow={1}
                swipeToSlide={true}
                focusOnSelect={true}
                arrows={true}
                infinite={false}
                nextArrow={<MdKeyboardArrowRight />}
                prevArrow={<MdKeyboardArrowLeft />}
                className="worksite-results-slider"
                beforeChange={(_, next) => setCurrentIndex(next)}
                initialSlide={currentIndex}
            >
                {worksitesWithData.map((worksite) => (
                    <div className="worksite-results-slider-item" key={worksite.id}>
                        <Typography intrinsic="h3" className="mb-x-small-spacing">
                            {worksite.progress.worksite.name}
                        </Typography>
                        <div className="worksite-results-slider-item-info">
                            <WorksiteStatus status="progress" />
                        </div>
                    </div>
                ))}
            </Slider>

            <Container greyed className="worksite-container-wrapper">
                <div className="worksite-container" key={currentIndex}>
                    <CollapsableContainer title="Informations du chantier" defaultOpen={true} className="mb-medium-spacing">
                        <div className="worksite-container-results">
                            <div className="worksite-container-results-item">
                                <Typography intrinsic="span" color="grey">
                                    Chantier
                                </Typography>
                                <Typography intrinsic="h5">{currentWorksite.progress.worksite.name}</Typography>
                            </div>

                            <div className="worksite-container-results-item">
                                <Typography intrinsic="span" color="grey">
                                    Opérateur
                                </Typography>
                                <Typography intrinsic="h5">{currentWorksite.info.operator_name}</Typography>
                            </div>

                            <div className="worksite-container-results-item">
                                <Typography intrinsic="span" color="grey">
                                    Nb de logements
                                </Typography>
                                <Typography intrinsic="h5">{currentWorksite.info.apartments_count}</Typography>
                            </div>

                            <div className="worksite-container-results-item">
                                <Typography intrinsic="span" color="grey">
                                    Identifiant
                                </Typography>
                                <Typography intrinsic="h5">{currentWorksite.progress.worksite.id_ext}</Typography>
                            </div>
                        </div>
                    </CollapsableContainer>
                    <MinimalHeaderToggle labels={["Vue d'ensemble", 'Vue détaillée']}>
                        <>
                            <Container bordered="grey" className="worksite-details-container">
                                <Typography intrinsic="h4" defaultSpacing>
                                    Avancement du chantier
                                </Typography>
                                <div className="worksite-details-container-wrapper">
                                    {tasks.map((task) => (
                                        <Container
                                            key={task.name}
                                            padding="small-medium"
                                            bordered="grey"
                                            className="worksite-details-container-items"
                                        >
                                            <Typography intrinsic="h4" className="mb-small-medium-spacing">
                                                {task.name}
                                            </Typography>
                                            <div className="worksite-details-container-items-wrapper">
                                                <Container padding="none" className="worksite-details-container-item">
                                                    <Typography intrinsic="span" color="grey">
                                                        Consommation
                                                    </Typography>
                                                    <div className="flex-result">
                                                        <Typography intrinsic="h5">{Math.round(task.consummed)}h</Typography>
                                                        <RecapItem
                                                            amount={Math.round(task.percent_consumed).toString()}
                                                            unit="%"
                                                            ratio={getRatio(task.percent_consumed)}
                                                        />
                                                    </div>
                                                </Container>

                                                <Container padding="none" className="worksite-details-container-item">
                                                    <Typography intrinsic="span" color="grey">
                                                        Budget
                                                    </Typography>
                                                    <div className="flex-result">
                                                        <Typography intrinsic="h5">{Math.round(task.total)}h</Typography>
                                                    </div>
                                                </Container>

                                                <Container padding="none" className="worksite-details-container-item">
                                                    <Typography intrinsic="span" color="grey">
                                                        Phase
                                                    </Typography>
                                                    <div className="flex-result">
                                                        <Typography intrinsic="h5" color={getStatusColor(task.status)}>
                                                            {getStatusLabel(task.status)}
                                                        </Typography>
                                                    </div>
                                                </Container>
                                            </div>
                                        </Container>
                                    ))}
                                </div>
                            </Container>
                            <div
                                className={`worksite-recap ${
                                    currentWorksite.progress.global.consummed <= currentWorksite.progress.global.total
                                        ? 'positive'
                                        : 'negative'
                                }`}
                            >
                                <div className="worksite-recap-content">
                                    <Typography intrinsic="h4" className="mb-small-medium-spacing">
                                        Bilan global
                                    </Typography>

                                    <div className="worksite-details-container-items-wrapper">
                                        <Container padding="none" className="worksite-details-container-item">
                                            <Typography intrinsic="span" color="grey">
                                                Consommation
                                            </Typography>
                                            <div className="flex-result">
                                                <Typography intrinsic="h5">
                                                    {Math.round(currentWorksite.progress.global.consummed)}h
                                                </Typography>
                                                <RecapItem
                                                    amount={Math.round(currentWorksite.progress.global.percent_consumed).toString()}
                                                    unit="%"
                                                    ratio={getRatio(currentWorksite.progress.global.percent_consumed)}
                                                />
                                            </div>
                                        </Container>

                                        <Container padding="none" className="worksite-details-container-item">
                                            <Typography intrinsic="span" color="grey">
                                                Budget
                                            </Typography>
                                            <div className="flex-result">
                                                <Typography intrinsic="h5">{Math.round(currentWorksite.progress.global.total)}h</Typography>
                                            </div>
                                        </Container>

                                        <Container padding="none" className="worksite-details-container-item">
                                            <Typography intrinsic="span" color="grey">
                                                Différence
                                            </Typography>
                                            <div className="flex-result">
                                                <RecapItem
                                                    amount={
                                                        Math.round(
                                                            currentWorksite.progress.global.total -
                                                                currentWorksite.progress.global.consummed
                                                        ) > 0
                                                            ? `+${Math.round(currentWorksite.progress.global.total - currentWorksite.progress.global.consummed)}`
                                                            : Math.round(
                                                                  currentWorksite.progress.global.total -
                                                                      currentWorksite.progress.global.consummed
                                                              ).toString()
                                                    }
                                                    unit="h"
                                                    ratio={getRatio(currentWorksite.progress.global.percent_consumed)}
                                                />
                                            </div>
                                        </Container>
                                    </div>
                                </div>
                            </div>
                        </>
                        <>
                            <Container bordered="grey" className="worksite-details-container">
                                <Typography intrinsic="h4" defaultSpacing>
                                    Vue détaillée - Bientôt disponible
                                </Typography>
                                <Typography intrinsic="p" color="grey">
                                    La vue par logement sera bientôt disponible.
                                </Typography>
                            </Container>
                        </>
                    </MinimalHeaderToggle>
                </div>
            </Container>
        </div>
    );
};
